import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { auth } from "@/lib/firebase";

// Firebase-auth context. Provides the current user, a fresh ID token, and
// the basic credential methods used by onboarding and the tenant login screen.
interface AuthContextValue {
  user: User | null;
  idToken: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
  deleteCurrentUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ROOT CAUSE the SPA was hitting: Firebase persists the User in IndexedDB
    // and re-hydrates it on page load WITHOUT validating that the underlying
    // account still exists. getIdToken() then returns the cached signed JWT
    // (valid until expiry, ~1h) WITHOUT a network call — so an account that
    // was deleted server-side keeps the SPA in a "signed in" state with a
    // signature-valid token that no real user backs.
    //
    // Catching getIdToken() errors alone isn't enough: the cached token is
    // valid for an hour, so the rejection only fires after a full hour OR
    // on the SDK's internal accounts:lookup probe — and the SDK doesn't
    // propagate that probe's 400 into onIdTokenChanged.
    //
    // The fix: on the FIRST onIdTokenChanged callback that hands us a
    // truthy user, force a server round-trip via User.reload(). That hits
    // accounts:lookup synchronously from our code, and we can react. If the
    // account is gone, we get auth/user-not-found / auth/user-disabled /
    // auth/user-token-expired and we signOut once. Subsequent
    // onIdTokenChanged(null) cycles back through cleanly.
    //
    // Reload is only triggered once per app session (didPreflight ref). After
    // that, normal onIdTokenChanged behaviour resumes — the SDK's own ~1h
    // refresh cycle is fine for catching mid-session account deletions.
    let didPreflight = false;

    const unsub = onIdTokenChanged(auth, async (u) => {
      if (u && !didPreflight) {
        didPreflight = true;
        try {
          // Force a network token refresh. We deliberately use
          // getIdToken(forceRefresh=true) instead of User.reload() because
          // reload() calls accounts:lookup, which can resolve silently in
          // some Firebase SDK paths (e.g. when the response is 200 with an
          // empty users array — the SDK maps it to auth/internal-error but
          // doesn't always reject). getIdToken(true) hits the
          // secure-token endpoint, which is strict: if the underlying
          // account no longer exists or has been disabled, the request
          // rejects with auth/user-token-expired or auth/user-disabled and
          // the SDK auto-clears auth.currentUser internally.
          //
          // The result of the refreshed token is set into idToken below
          // (the second getIdToken() returns the just-refreshed value from
          // cache), so this isn't an extra round-trip.
          await u.getIdToken(true);
        } catch (err) {
          const code = (err as { code?: string }).code ?? "";
          // Network errors are transient — leave the cached user in place
          // and let the next refresh retry. Anything else means the account
          // is genuinely unusable and we sign out.
          if (code !== "auth/network-request-failed") {
            console.warn("auth: pre-flight token refresh rejected — signing out", code);
            try {
              await fbSignOut(auth);
            } catch {
              /* best-effort */
            }
            // The signOut triggers onIdTokenChanged(null), which re-enters
            // this callback and lands in the else branch below. Bail here
            // so we don't paint the doomed truthy state for a render.
            return;
          }
          console.warn("auth: pre-flight token refresh network failed (transient)", code);
        }
      }

      setUser(u);
      if (u) {
        try {
          const t = await u.getIdToken();
          setIdToken(t);
        } catch (err) {
          // Mid-session detection. If the SDK's ~1h refresh fails after the
          // account is deleted, this catches it without waiting for a page
          // reload. Same allowlist logic as the pre-flight, plus a couple
          // of token-lifecycle codes for completeness.
          const code = (err as { code?: string }).code ?? "";
          const stale =
            code === "auth/user-token-expired" ||
            code === "auth/user-not-found" ||
            code === "auth/user-disabled" ||
            code === "auth/invalid-user-token" ||
            code === "auth/id-token-expired" ||
            code === "auth/id-token-revoked";
          if (stale) {
            console.warn("auth: stale firebase user — forcing signOut", code);
            try {
              await fbSignOut(auth);
            } catch {
              /* best-effort; the SDK will catch up on the next tick */
            }
          } else {
            console.warn("auth: getIdToken failed", err);
          }
          setIdToken(null);
        }
      } else {
        setIdToken(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // Verification email is sent by the backend (token-based link → /onboarding/verify).
    // Firebase's sendEmailVerification is intentionally NOT called here — sending
    // both produced two emails with conflicting links and let the user advance
    // without the backend's authoritative {domain, domain_exists} payload.
    return cred.user;
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
  }, []);

  const reload = useCallback(async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    const t = await auth.currentUser.getIdToken(true);
    setUser(auth.currentUser);
    setIdToken(t);
  }, []);

  // Roll back the just-created Firebase user — used when signUp succeeds but
  // backend onboarding.register fails, so the email isn't burned by an
  // orphaned Firebase account that the user can't re-register against.
  const deleteCurrentUser = useCallback(async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.delete();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, idToken, loading, signIn, signUp, signOut, reload, deleteCurrentUser }),
    [user, idToken, loading, signIn, signUp, signOut, reload, deleteCurrentUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
